import React from 'react';
import styled, { css } from 'styled-components';
import { media } from '../theme/tokens';

interface Column<T> {
  key: string;
  header: string;
  render: (row: T, index: number) => React.ReactNode;
  width?: string;
  sortable?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  unavailable?: boolean;
  emptyMessage?: string;
}

export function DataTable<T extends { id?: string | number }>({
  columns,
  data,
  onRowClick,
  unavailable = false,
  emptyMessage = 'No data'
}: DataTableProps<T>) {
  if (unavailable) {
    return (
      <UnavailableWrap>
        <UnavailableInner>
          <UnavailableIcon>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M9 21V9" />
            </svg>
          </UnavailableIcon>
          <UnavailableText>Table data not available</UnavailableText>
          <UnavailableHint>
            This view requires API data not yet connected.
          </UnavailableHint>
        </UnavailableInner>
      </UnavailableWrap>
    );
  }

  return (
    <TableWrap>
      <ScrollContainer>
        <Table>
          <thead>
            <tr>
              {columns.map((col) => (
                <Th key={col.key} style={{ width: col.width }}>
                  {col.header}
                  {col.sortable && <SortIcon>↕</SortIcon>}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <Td colSpan={columns.length}>
                  <EmptyRow>{emptyMessage}</EmptyRow>
                </Td>
              </tr>
            ) : (
              data.map((row, i) => (
                <Tr
                  key={row.id ?? i}
                  onClick={() => onRowClick?.(row)}
                  $clickable={!!onRowClick}
                >
                  {columns.map((col) => (
                    <Td key={col.key}>{col.render(row, i)}</Td>
                  ))}
                </Tr>
              ))
            )}
          </tbody>
        </Table>
      </ScrollContainer>
    </TableWrap>
  );
}

const TableWrap = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  overflow: hidden;
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  min-width: 600px;
`;

const Th = styled.th`
  background: var(--paper);
  text-align: left;
  padding: 10px 14px;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  font-weight: 600;
  border-bottom: 1px solid var(--line);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: var(--ink);
  }
`;

const SortIcon = styled.span`
  opacity: 0.4;
  margin-left: 4px;
  font-size: 10px;
`;

const Tr = styled.tr<{ $clickable: boolean }>`
  border-bottom: 1px solid var(--line-soft);
  transition: background 0.1s;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: var(--paper-warm);
  }
`;

const Td = styled.td`
  padding: 14px;
  vertical-align: middle;
`;

const EmptyRow = styled.div`
  text-align: center;
  color: var(--ink-faint);
  padding: 32px 0;
`;

const UnavailableWrap = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  opacity: 0.5;
  filter: grayscale(0.6);
`;

const UnavailableInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 8px;
`;

const UnavailableIcon = styled.div`
  color: var(--ink-faint);
  margin-bottom: 4px;
`;

const UnavailableText = styled.div`
  font-weight: 600;
  color: var(--ink-muted);
  font-size: 14px;
`;

const UnavailableHint = styled.div`
  font-size: 12px;
  color: var(--ink-faint);
  font-style: italic;
`;
